import apiClient from 'api/apiClient';
import {
  ProgrammeSummary,
  ProgrammeAssignment,
  AssignmentProgressRequest,
  ProgrammeScore,
  FinancialSummaryResponse,
  DailySpendSummaryResponse,
  TransactionResponse,
  CommitmentResponse,
  CommitmentRequest,
  GoalResponse,
  GoalRequest,
  GoalMilestone,
  GoalMilestoneRequest,
  MonthlyTargetResponse,
  SaveTargetsRequest,
  FinancialProfileResponse,
  SaveProfileRequest,
  NetWorthResponse,
  BankAccountResponse,
  CoachInsight,
  CoachAction,
  CoachRecommendation,
  CSVImportRequest,
  CreateTransactionRequest,
  HouseholdSummary,
  HouseholdMember,
  HouseholdSharing,
  HouseholdInsight,
  HouseholdInviteRequest,
  EntitlementResponse,
  ForecastResponse,
  UserAchievementResponse,
} from './types';

const BASE =
  process.env.REACT_APP_USER_REST_API_BASE_URL ??
  'http://localhost:8080/api/v4/user';

export const getFinancialSummary = (year?: number, month?: number) => {
  let url = `${BASE}/financial/summary`;
  if (year && month) url += `?year=${year}&month=${month}`;
  return apiClient.get<FinancialSummaryResponse>(url);
};

export const getDailySummary = (year: number, month: number) =>
  apiClient.get<DailySpendSummaryResponse>(
    `${BASE}/financial/daily-summary?year=${year}&month=${month}`,
  );

export const getTransactions = (year?: number, month?: number) => {
  let url = `${BASE}/financial/transactions`;
  if (year && month) url += `?year=${year}&month=${month}`;
  return apiClient.get<TransactionResponse[]>(url);
};

export const getCommitments = () =>
  apiClient.get<CommitmentResponse[]>(`${BASE}/financial/commitments`);

export const getGoals = (status?: string) => {
  let url = `${BASE}/financial/goals`;
  if (status) url += `?status=${status}`;
  return apiClient.get<GoalResponse[]>(url);
};

export const patchTransactionCategory = (id: number, category: string) =>
  apiClient.patch<TransactionResponse>(
    `${BASE}/financial/transactions/${id}/category`,
    { category },
  );

export const createTransaction = (payload: CreateTransactionRequest) =>
  apiClient.post<TransactionResponse>(
    `${BASE}/financial/transactions`,
    payload,
  );

export const getBankAccounts = () =>
  apiClient.get<BankAccountResponse[]>(`${BASE}/financial/bank/accounts`);

export const deleteBankAccount = (accountId: number) =>
  apiClient.delete(`${BASE}/financial/bank/accounts/${accountId}`);

export const importCSV = (payload: CSVImportRequest) =>
  apiClient.post(`${BASE}/financial/transactions/import`, payload);

export const getCoachInsights = () =>
  apiClient.get<CoachInsight[]>(`${BASE}/financial/coach/insights`);

export const getCoachActions = () =>
  apiClient.get<CoachAction[]>(`${BASE}/financial/coach/actions`);

export const getCoachRecommendations = () =>
  apiClient.get<CoachRecommendation[]>(
    `${BASE}/financial/coach/recommendations`,
  );

export const completeCoachAction = (key: string) =>
  apiClient.post(`${BASE}/financial/coach/actions/${key}/complete`);

// ─── Targets / Profile ────────────────────────────────────────────────────────

export const getTargets = (year: number, month: number) =>
  apiClient.get<MonthlyTargetResponse>(
    `${BASE}/financial/targets?year=${year}&month=${month}`,
  );

export const getSuggestedTargets = (year: number, month: number) =>
  apiClient.get<MonthlyTargetResponse>(
    `${BASE}/financial/targets/suggested?year=${year}&month=${month}`,
  );

export const saveTargets = (payload: SaveTargetsRequest) =>
  apiClient.put<MonthlyTargetResponse>(`${BASE}/financial/targets`, payload);

export const getFinancialProfile = () =>
  apiClient.get<FinancialProfileResponse>(`${BASE}/financial/profile`);

export const saveFinancialProfile = (payload: SaveProfileRequest) =>
  apiClient.put<FinancialProfileResponse>(`${BASE}/financial/profile`, payload);

// ─── Commitments CRUD ─────────────────────────────────────────────────────────

export const createCommitment = (payload: CommitmentRequest) =>
  apiClient.post<CommitmentResponse>(`${BASE}/financial/commitments`, payload);

export const updateCommitment = (id: number, payload: CommitmentRequest) =>
  apiClient.put<CommitmentResponse>(
    `${BASE}/financial/commitments/${id}`,
    payload,
  );

export const deleteCommitment = (id: number) =>
  apiClient.delete(`${BASE}/financial/commitments/${id}`);

// ─── Goals CRUD ───────────────────────────────────────────────────────────────

export const createGoal = (payload: GoalRequest) =>
  apiClient.post<GoalResponse>(`${BASE}/financial/goals`, payload);

export const updateGoal = (id: number, payload: GoalRequest) =>
  apiClient.put<GoalResponse>(`${BASE}/financial/goals/${id}`, payload);

export const deleteGoal = (id: number) =>
  apiClient.delete(`${BASE}/financial/goals/${id}`);

export const contributeToGoal = (id: number, amount: number) =>
  apiClient.post<GoalResponse>(`${BASE}/financial/goals/${id}/contribute`, {
    amount,
  });

// ─── Goal Milestones ──────────────────────────────────────────────────────────

export const getMilestones = (goalId: number) =>
  apiClient.get<GoalMilestone[]>(`${BASE}/financial/goals/${goalId}/milestones`);

export const createMilestone = (goalId: number, payload: GoalMilestoneRequest) =>
  apiClient.post<GoalMilestone>(`${BASE}/financial/goals/${goalId}/milestones`, payload);

export const deleteMilestone = (goalId: number, milestoneId: number) =>
  apiClient.delete(`${BASE}/financial/goals/${goalId}/milestones/${milestoneId}`);

// ─── Transactions ─────────────────────────────────────────────────────────────

export const deleteTransaction = (id: number) =>
  apiClient.delete(`${BASE}/financial/transactions/${id}`);

// ─── Net worth ────────────────────────────────────────────────────────────────

export const getNetWorth = () =>
  apiClient.get<NetWorthResponse>(`${BASE}/financial/net-worth`);

// ─── Household ────────────────────────────────────────────────────────────────

export const getHousehold = () =>
  apiClient.get<HouseholdSummary>(`${BASE}/household`);

export const getHouseholdMembers = () =>
  apiClient.get<HouseholdMember[]>(`${BASE}/household/members`);

export const inviteToHousehold = (payload: HouseholdInviteRequest) =>
  apiClient.post(`${BASE}/household/invite`, payload);

export const acceptHouseholdInvite = (token: string) =>
  apiClient.post(`${BASE}/household/accept/${token}`);

export const removeHouseholdMember = (membershipId: number) =>
  apiClient.delete(`${BASE}/household/members/${membershipId}`);

export const getHouseholdSharing = () =>
  apiClient.get<HouseholdSharing>(`${BASE}/household/sharing`);

export const updateHouseholdSharing = (payload: HouseholdSharing) =>
  apiClient.put<HouseholdSharing>(`${BASE}/household/sharing`, payload);

export const getHouseholdInsights = () =>
  apiClient.get<HouseholdInsight[]>(`${BASE}/household/insights`);

// ─── Subscription / Entitlement ───────────────────────────────────────────────

export const getEntitlement = () =>
  apiClient.get<EntitlementResponse>(
    `${BASE}/financial/subscription/entitlement`,
  );

// ─── Programmes (mob/feature/docket) ─────────────────────────────────────────

const MOB_DOCKET =
  (process.env.REACT_APP_REST_API_BASE_URL ?? 'http://localhost:8080/api/v4') +
  '/mob/feature/docket';

export const getDocketProgrammes = () =>
  apiClient.get<ProgrammeSummary[]>(`${MOB_DOCKET}/programmes`);

export const getDocketProgramme = (code: string) =>
  apiClient.get<ProgrammeSummary>(`${MOB_DOCKET}/programmes/${code}`);

export const enrolDocketProgramme = (code: string) =>
  apiClient.post<ProgrammeSummary>(`${MOB_DOCKET}/programmes/${code}/enrol`);

export const getDocketAssignments = (code: string) =>
  apiClient.get<ProgrammeAssignment[]>(
    `${MOB_DOCKET}/programmes/${code}/assignments`,
  );

export const updateDocketAssignment = (
  id: number,
  progress: AssignmentProgressRequest,
) => apiClient.put<ProgrammeAssignment>(`${MOB_DOCKET}/assignments/${id}`, progress);

export const getDocketProgrammeScore = (code: string) =>
  apiClient.get<ProgrammeScore>(`${MOB_DOCKET}/programmes/${code}/score`);

// ─── Forecast ─────────────────────────────────────────────────────────────────

export const getForecast = (year?: number, month?: number) => {
  let url = `${BASE}/financial/forecast`;
  if (year && month) url += `?year=${year}&month=${month}`;
  return apiClient.get<ForecastResponse>(url);
};

// ─── Achievements ─────────────────────────────────────────────────────────────

export const getAchievements = () =>
  apiClient.get<UserAchievementResponse[]>(`${BASE}/financial/achievements`);

export const markAchievementSeen = (id: number) =>
  apiClient.put(`${BASE}/financial/achievements/${id}/seen`);

// ─── Business Goal Linkage ────────────────────────────────────────────────────

export const getBusinessGoals = (businessId: number) =>
  apiClient.get<import('./types').GoalResponse[]>(`${BASE}/business/${businessId}/goals`);
